/**
 * Email Domain Validation Utilities
 *
 * This module provides shared validation for email domains
 * to ensure consistency between client and server-side validation.
 */

import { z } from 'zod';

export type EmailDomainValidationResult = {
  isValid: boolean;
  domain: string;
  reason?: string;
  isTopProvider: boolean;
};

export type EmailProvider = {
  name: string;
  domains: string[];
};

/**
 * Top 10 largest email providers in the world with their domains
 * Based on market share and user base statistics
 */
export const TOP_EMAIL_PROVIDERS: EmailProvider[] = [
  {
    name: 'Gmail',
    domains: ['gmail.com', 'googlemail.com'],
  },
  {
    name: 'Outlook',
    domains: ['outlook.com', 'live.com', 'hotmail.com', 'msn.com'],
  },
  {
    name: 'Yahoo Mail',
    domains: ['yahoo.com', 'ymail.com', 'rocketmail.com'],
  },
  {
    name: 'Apple iCloud',
    domains: ['icloud.com', 'me.com', 'mac.com'],
  },
  {
    name: 'AOL Mail',
    domains: ['aol.com'],
  },
  {
    name: 'ProtonMail',
    domains: ['protonmail.com', 'proton.me'],
  },
  {
    name: 'GMX',
    domains: ['gmx.com', 'gmx.net', 'gmx.de', 'gmx.co.uk'],
  },
  {
    name: 'Mail.com',
    domains: ['mail.com'],
  },
  {
    name: 'Yandex',
    domains: ['yandex.com', 'yandex.ru', 'ya.ru'],
  },
  {
    name: 'Zoho Mail',
    domains: ['zoho.com', 'zohomail.com'],
  },
];

/**
 * Create a Set of all allowed domains for efficient lookup
 */
export const ALLOWED_DOMAINS = new Set(TOP_EMAIL_PROVIDERS.flatMap((provider) => provider.domains));

/**
 * Basic email format regex - defined at top level for performance
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Extract domain from email address
 */
export function extractDomain(email: string): string {
  const trimmedEmail = email.trim().toLowerCase();
  const atIndex = trimmedEmail.lastIndexOf('@');

  if (atIndex === -1 || atIndex === trimmedEmail.length - 1) {
    return '';
  }

  return trimmedEmail.substring(atIndex + 1);
}

/**
 * Check if a domain is from one of the top email providers
 */
export function isTopProviderDomain(domain: string): boolean {
  return ALLOWED_DOMAINS.has(domain.toLowerCase());
}

/**
 * Get the provider name for a given domain
 */
export function getProviderName(domain: string): string | null {
  const provider = TOP_EMAIL_PROVIDERS.find((p) => p.domains.includes(domain.toLowerCase()));
  return provider?.name || null;
}

/**
 * Validate email domain against top providers list
 */
export function validateEmailDomain(email: string): EmailDomainValidationResult {
  // Basic email format validation
  if (!EMAIL_REGEX.test(email)) {
    return {
      isValid: false,
      domain: '',
      reason: 'Invalid email format',
      isTopProvider: false,
    };
  }

  const domain = extractDomain(email);

  if (!domain) {
    return {
      isValid: false,
      domain: '',
      reason: 'Could not extract domain from email',
      isTopProvider: false,
    };
  }

  // Check if domain is from top providers
  if (isTopProviderDomain(domain)) {
    const providerName = getProviderName(domain);
    return {
      isValid: true,
      domain,
      isTopProvider: true,
      reason: `Allowed provider: ${providerName}`,
    };
  }

  return {
    isValid: false,
    domain,
    reason: `Email domain "${domain}" is not from a supported provider. Please use an email from one of the top providers: ${TOP_EMAIL_PROVIDERS.map((p) => p.name).join(', ')}`,
    isTopProvider: false,
  };
}

/**
 * Check if custom allowed domains are configured in environment
 * Server-side only function
 */
export function getCustomAllowedDomains(): string[] {
  if (typeof process === 'undefined' || !process.env) {
    return [];
  }

  const customDomains = process.env.ALLOWED_EMAIL_DOMAINS;
  if (!customDomains) {
    return [];
  }

  return customDomains
    .split(',')
    .map((domain) => domain.trim().toLowerCase())
    .filter((domain) => domain.length > 0);
}

/**
 * Get all allowed domains (top providers + custom domains)
 * Server-side only function
 */
export function getAllAllowedDomains(): string[] {
  const topProviderDomains = Array.from(ALLOWED_DOMAINS);
  const customDomains = getCustomAllowedDomains();

  return [...new Set([...topProviderDomains, ...customDomains])];
}

/**
 * Enhanced validation that includes custom domains from environment
 * Server-side only function
 */
export function validateEmailDomainWithCustom(email: string): EmailDomainValidationResult {
  const basicValidation = validateEmailDomain(email);

  // If it's already valid (top provider), return early
  if (basicValidation.isValid) {
    return basicValidation;
  }

  // Check custom domains
  const customDomains = getCustomAllowedDomains();
  const domain = extractDomain(email);

  if (customDomains.includes(domain.toLowerCase())) {
    return {
      isValid: true,
      domain,
      isTopProvider: false,
      reason: `Allowed custom domain: ${domain}`,
    };
  }

  // Return the original validation result with updated reason
  const allAllowedDomains = getAllAllowedDomains();
  return {
    ...basicValidation,
    reason: `Email domain "${domain}" is not allowed. Please use an email from one of the supported providers or custom domains: ${allAllowedDomains.join(', ')}`,
  };
}

/**
 * Zod schema for email validation with domain checking
 */
export const emailWithDomainSchema = z
  .string()
  .email('Invalid email address')
  .refine(
    (email: string) => {
      const validation = validateEmailDomain(email);
      return validation.isValid;
    },
    {
      message: 'Email domain not supported',
    }
  );

/**
 * Real-time email validation hook for forms
 * Returns a validation function that can be used in form validators
 */
export function createEmailDomainValidator() {
  return (email: string) => {
    const validation = validateEmailDomain(email);

    if (!validation.isValid) {
      return validation.reason || 'Email domain not supported';
    }

    return; // No error if valid
  };
}

/**
 * Get user-friendly message for supported providers
 */
export function getSupportedProvidersMessage(): string {
  return `We support emails from: ${TOP_EMAIL_PROVIDERS.map((p) => p.name).join(', ')}`;
}

/**
 * Check if email is from a specific provider
 */
export function isFromProvider(email: string, providerName: string): boolean {
  const domain = extractDomain(email);
  const provider = TOP_EMAIL_PROVIDERS.find((p) => p.name === providerName);

  if (!provider) {
    return false;
  }

  return provider.domains.includes(domain);
}

/**
 * Get provider info for an email
 */
export function getEmailProviderInfo(email: string): {
  name: string | null;
  domain: string;
  isSupported: boolean;
} {
  const domain = extractDomain(email);
  const providerName = getProviderName(domain);

  return {
    name: providerName,
    domain,
    isSupported: isTopProviderDomain(domain),
  };
}
